/**
 * Tipos de datos para el dashboard (portados del legacy)
 */

export interface RawRow {
  cuenta: string;
  rubro: string;
  tipo: string;
  cc: string;
  periodo: string;
  importe: number;
}

/** Fila de una pestaña del sheet mapeada por headers (ventas/compras). */
export type SheetRow = Record<string, string | null>;

export type Distribution = {
  Merchandising: number;
  Impresiones: number;
  "DTF Textil": number;
  [key: string]: number;
};

/**
 * Normaliza los nombres de los rubros
 */
export function normRubro(r: string | null): string {
  if (!r) return "";
  const norm = String(r).trim();
  if (norm.includes("Comercializ")) return "Gastos de Comercialización";
  return norm;
}

/**
 * Normaliza el formato de periodo (YYYY/MM)
 */
export function normPeriodo(v: string | number | null | undefined): string {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}\/\d{2}$/.test(s)) return s;
  if (/^\d{4}\/\d{1}$/.test(s)) return s.replace(/(\d{4})\/(\d)$/, "$1/0$2");

  const m = s.match(/^(\d{4})[\/\-](\d{1,2})/);
  if (m?.[1] && m[2]) return `${m[1]}/${m[2].padStart(2, "0")}`;

  const n = Number(s);
  if (!Number.isNaN(n) && n > 40000) {
    const d = new Date(Math.round((n - 25569) * 86400 * 1000));
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  return s;
}

/**
 * Formateo de números (Portado línea 540)
 */
export function fmt(v: number, d = 2): string {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(d)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

export function fmtFull(v: number): string {
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(v));
}

export function cls(v: number): "pos" | "neg" | "neu" {
  if (v > 0) return "pos";
  if (v < 0) return "neg";
  return "neu";
}

/**
 * Convierte una fecha cruda del sheet a período YYYY/MM.
 * Portado de legacy dateToPeriod (línea 985).
 */
export function dateToPeriod(v: string | null | undefined): string {
  if (!v) return "";
  const s = String(v).trim();
  // dd/mm/yyyy
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (m?.[3] && m[2]) return `${m[3]}/${m[2].padStart(2, "0")}`;
  // yyyy-mm-dd
  const m2 = s.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})$/);
  if (m2?.[1] && m2[2]) return `${m2[1]}/${m2[2].padStart(2, "0")}`;
  return normPeriodo(v);
}

/**
 * Formatea una fecha para mostrar en tablas (DD/MM/YYYY).
 * Port literal del legacy fmtDate (línea 996).
 * Solo reconoce el formato dd/mm/yyyy; cualquier otro valor (ej. yyyy-mm-dd,
 * serial numérico de Excel, texto libre) se devuelve sin modificar.
 * No normaliza otros formatos — usar dateToPeriod() para conversión de períodos.
 */
export function fmtDate(v: string | null | undefined): string {
  if (!v) return "";
  const s = String(v).trim();
  // Ya está en formato dd/mm/yyyy → devolver tal cual
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  return s;
}

/**
 * Lógica de prorrateo de Estructura
 */
export function computeImporte(r: RawRow, cc: string, distribution: Distribution): number {
  if (r.cc !== "Estructura") return r.importe;
  if (cc === "Total") return r.importe;
  return r.importe * (distribution[cc] || 0);
}
